"use client";

import { useEffect, useRef, useState } from "react";
import type { ReviewItem } from "@/lib/api/projection-types";
import type { ContentQueueItem } from "@/lib/api";
import { LIFE_AREA_META } from "@/lib/lifeAreas";
import {
  deriveSourceFromUrl,
  formatSavedFrom,
  libraryItemSource,
  libraryTypeKey,
  LIBRARY_TYPE_LABEL,
} from "@/lib/libraryFormatters";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import LibraryTypeIcon from "./LibraryTypeIcon";

interface Props {
  item: ReviewItem;
  detail?: ContentQueueItem;
  recommended: boolean;
  selected: boolean;
  selecting: boolean;
  busy: boolean;
  onToggleSelect: (id: string) => void;
  onOpenDetail: (id: string) => void;
  onEdit: (id: string) => void;
  onLinkGoal: (id: string) => void;
  onAddToTodo: (item: ReviewItem) => void;
  onAddToSchedule: (item: ReviewItem) => void;
  onDismiss: (item: ReviewItem) => void;
}

export default function LibraryCard({
  item,
  detail,
  recommended,
  selected,
  selecting,
  busy,
  onToggleSelect,
  onOpenDetail,
  onEdit,
  onLinkGoal,
  onAddToTodo,
  onAddToSchedule,
  onDismiss,
}: Props) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!overflowOpen) return;
    function handleDocClick(event: MouseEvent) {
      if (!overflowRef.current) return;
      if (!overflowRef.current.contains(event.target as Node)) {
        setOverflowOpen(false);
      }
    }
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, [overflowOpen]);

  const title = sanitizeSurfaceTitle(item.title);
  const goalTitle = item.goal_title ? sanitizeSurfaceTitle(item.goal_title) : null;
  const areaMeta = item.goal_category ? LIFE_AREA_META[item.goal_category] : null;
  const typeKey = libraryTypeKey(item.content_type ?? detail?.content_type);
  const url = item.url ?? detail?.url ?? null;
  const source = deriveSourceFromUrl(url, "");
  const sourceKind = libraryItemSource(typeKey, Boolean(item.url ?? detail?.url));
  const savedFrom = formatSavedFrom(detail?.saved_at ?? null, sourceKind);
  const because = recommended ? detail?.priority_reason ?? null : null;
  const description = (item.description ?? detail?.description ?? "").trim();
  const snippet = description || (!recommended ? detail?.priority_reason ?? null : null);

  const cardClass = recommended
    ? "border-amber bg-sand-pale"
    : "border-border bg-card";
  const selectedClass = selected
    ? "ring-2 ring-forest-med shadow-md"
    : "shadow-sm hover:shadow-md";

  return (
    <article
      data-testid={item.primary_action === "do_next" ? "review-backlog-item" : "review-worth-parking-card"}
      data-reveal-id={item.id}
      data-recommended={recommended ? "true" : "false"}
      data-library-card={recommended ? "recommended" : "saved"}
      className={`group relative flex flex-col rounded-2xl border p-4 transition-shadow cursor-pointer ${cardClass} ${selectedClass}`}
      onClick={() => onToggleSelect(item.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggleSelect(item.id);
        }
      }}
      aria-pressed={selected}
      aria-label={`${recommended ? "Recommended: " : ""}${title}`}
    >
      <div className="flex items-start gap-2">
        <div
          aria-hidden
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
            recommended ? "border border-amber/40 bg-white/70" : "border border-border bg-bg"
          } text-text-light`}
        >
          <LibraryTypeIcon type={typeKey} size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 text-[11px] truncate">
            {areaMeta ? (
              <span className={`font-semibold capitalize ${recommended ? "text-earth" : "text-forest-med"}`}>
                {areaMeta.emoji} {areaMeta.label}
              </span>
            ) : (
              <span className="text-muted">Unfiled</span>
            )}
            {source && (
              <>
                <span className="text-muted">·</span>
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    className="truncate text-muted underline underline-offset-2 hover:text-forest-med"
                    title={url}
                  >
                    {source}
                  </a>
                ) : (
                  <span className="truncate text-muted">{source}</span>
                )}
              </>
            )}
          </div>
          {savedFrom && (
            <div className="mt-0.5 truncate text-[10.5px] text-muted">{savedFrom}</div>
          )}
        </div>
        <Checkbox
          checked={selected}
          visible={selecting || selected}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect(item.id);
          }}
        />
        <button
          type="button"
          aria-label="Remove from library"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(item);
          }}
          className="ml-1 flex h-6 w-6 items-center justify-center rounded-full text-muted/70 opacity-0 transition-opacity hover:bg-secondary hover:text-text group-hover:opacity-100"
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {recommended && (
        <span className="mt-3 inline-flex w-fit items-center gap-1 rounded-full bg-amber/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-earth">
          ★ Recommended for you
        </span>
      )}

      <h3 className="mt-2 text-sm font-semibold leading-snug text-text line-clamp-2">
        {title}
      </h3>

      {snippet && (
        <p className="mt-2 text-[12.5px] leading-relaxed text-text-light line-clamp-4">
          {snippet}
        </p>
      )}

      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="library-card-url"
          onClick={(event) => event.stopPropagation()}
          className="mt-2 inline-flex max-w-full items-center gap-1.5 self-start rounded-full border border-border bg-bg px-2.5 py-1 text-[11px] font-medium text-blue underline underline-offset-2 hover:border-forest-med/30 hover:text-forest-med"
          title={url}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <span className="truncate">{source || url}</span>
        </a>
      )}

      {because && (
        <p
          data-testid="library-because"
          className="mt-2 rounded-r border-l-2 border-amber bg-white/60 py-1.5 pl-3 pr-2 text-[12px] italic leading-snug text-earth"
        >
          {because}
        </p>
      )}

      {goalTitle && (
        <p className="mt-2 truncate text-[11.5px] text-muted">
          ◎ Tied to <span className="font-semibold text-forest-med">{goalTitle}</span>
        </p>
      )}

      <div className="mt-3 flex items-center gap-2 border-t border-dashed border-border/70 pt-3">
        <button
          type="button"
          data-testid="review-place-schedule"
          onClick={(e) => {
            e.stopPropagation();
            onAddToSchedule(item);
          }}
          disabled={busy}
          className="rounded-lg bg-forest-med px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-forest disabled:opacity-60"
        >
          Schedule
        </button>
        <button
          type="button"
          data-testid="review-place-today"
          onClick={(e) => {
            e.stopPropagation();
            onAddToTodo(item);
          }}
          disabled={busy}
          className="rounded-lg border border-border bg-white px-3 py-1.5 text-[12px] font-medium text-text transition-colors hover:border-forest-med/40 hover:text-forest-med disabled:opacity-60"
        >
          + Todo
        </button>
        <span className="flex-1" />
        <div ref={overflowRef} className="relative">
          <button
            type="button"
            data-testid="library-card-overflow"
            aria-haspopup="menu"
            aria-expanded={overflowOpen}
            aria-label="More actions"
            onClick={(e) => {
              e.stopPropagation();
              setOverflowOpen((v) => !v);
            }}
            className="flex h-7 w-7 items-center justify-center rounded-full text-muted transition-colors hover:bg-secondary hover:text-forest-med"
          >
            <span className="text-base leading-none">···</span>
          </button>
          {overflowOpen && (
            <div
              role="menu"
              data-testid="library-card-overflow-menu"
              className="absolute bottom-full right-0 mb-1 w-48 overflow-hidden rounded-xl border border-border bg-white shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <OverflowItem
                testId="library-overflow-view"
                onClick={() => {
                  setOverflowOpen(false);
                  onOpenDetail(item.id);
                }}
              >
                ↗ View details
              </OverflowItem>
              <OverflowItem
                testId="library-overflow-edit"
                onClick={() => {
                  setOverflowOpen(false);
                  onEdit(item.id);
                }}
              >
                ✎ Edit info
              </OverflowItem>
              <OverflowItem
                testId="library-overflow-link-goal"
                onClick={() => {
                  setOverflowOpen(false);
                  onLinkGoal(item.id);
                }}
              >
                ◎ Link to goal
              </OverflowItem>
              <OverflowItem
                testId="library-overflow-archive"
                danger
                onClick={() => {
                  setOverflowOpen(false);
                  onDismiss(item);
                }}
              >
                × Archive
              </OverflowItem>
            </div>
          )}
        </div>
      </div>

      {(item.duration_min ?? detail?.duration_min) && (
        <span className="absolute bottom-3 right-3 hidden text-[10px] font-medium text-muted">
          {item.duration_min ?? detail?.duration_min}m
        </span>
      )}

      <span className="sr-only">{LIBRARY_TYPE_LABEL[typeKey]}</span>
    </article>
  );
}

function OverflowItem({
  testId,
  onClick,
  danger,
  children,
}: {
  testId?: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
        danger
          ? "text-rose hover:bg-rose/10"
          : "text-text hover:bg-bg"
      }`}
    >
      {children}
    </button>
  );
}

function Checkbox({
  checked,
  visible,
  onClick,
}: {
  checked: boolean;
  visible: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={onClick}
      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded transition-opacity ${
        checked
          ? "bg-forest-med text-white opacity-100"
          : "border border-border bg-white text-transparent hover:border-forest-med/40"
      } ${visible ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
    >
      {checked && (
        <svg width="11" height="11" viewBox="0 0 12 12">
          <path d="M2 6l3 3 5-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}
